import { useState, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loader2, ArrowLeft, CheckCircle, AlertCircle, Trash2, Upload, Users, Plus, ShieldCheck, ShieldAlert, FileText, ExternalLink, Globe, Settings2, Pencil } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { OrgCharterView } from './OrgCharterView'
import { CreateUserSheet } from './CreateUserSheet'
import { OrgUnitTree } from '@/components/Organisation/OrgUnitTree'
import { ConorbitLoader } from '@/components/ui/ConorbitLoader'

const ALL_LANGUAGES = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'fr', label: 'French',     flag: '🇫🇷' },
  { code: 'pt', label: 'Portuguese', flag: '🇵🇹' },
  { code: 'es', label: 'Spanish',    flag: '🇪🇸' },
  { code: 'ar', label: 'Arabic',     flag: '🇸🇦' },
  { code: 'sw', label: 'Swahili',    flag: '🇰🇪' },
]

const MODULES: { key: string; label: string }[] = [
  { key: 'dashboard',          label: 'Dashboard' },
  { key: 'media_registry',     label: 'Media Registry' },
  { key: 'accreditation',      label: 'Accreditation' },
  { key: 'events',             label: 'Events' },
  { key: 'coverage_analytics', label: 'Coverage Analytics' },
  { key: 'awards',             label: 'Awards' },
  { key: 'social_media',       label: 'Social Media' },
  { key: 'organogram',         label: 'Organogram' },
  { key: 'media_kits',         label: 'Media Kits' },
  { key: 'journalist_portal',  label: 'Journalist Portal' },
]

const ACCESS_LEVELS = ['none', 'view', 'edit', 'full'] as const

const DEFAULT_ROLE_PERMS = () =>
  Object.fromEntries(MODULES.map(m => [m.key, 'none'])) as Record<string, string>

const ONBOARDING_STEPS: { key: string; label: string }[] = [
  { key: 'cco_assigned',       label: 'Institution Admin appointed' },
  { key: 'brand_set',          label: 'Brand colours configured' },
  { key: 'charter_uploaded',   label: 'Institution charter uploaded' },
  { key: 'first_member',       label: 'First team member added' },
  { key: 'first_journalist',   label: 'First journalist registered' },
  { key: 'first_event',        label: 'First event created' },
  { key: 'media_kit_uploaded', label: 'Media kit uploaded' },
]

interface OrgProfileViewProps {
  orgId:   string
  onBack?: () => void
}

export function OrgProfileView({ orgId, onBack }: OrgProfileViewProps) {
  const { toast }      = useToast()
  const queryClient    = useQueryClient()
  const logoInputRef   = useRef<HTMLInputElement>(null)
  const [addUserOpen,  setAddUserOpen]  = useState(false)
  const [addUserRole,  setAddUserRole]  = useState<string | undefined>()
  const [savingSettings, setSavingSettings] = useState(false)
  const [uploadingLogo, setUploadingLogo]   = useState(false)
  const [deleting,      setDeleting]        = useState(false)
  // Custom role form
  const [showRoleForm,   setShowRoleForm]   = useState(false)
  const [editingRoleId,  setEditingRoleId]  = useState<string | null>(null)
  const [roleFormName,   setRoleFormName]   = useState('')
  const [roleFormDesc,   setRoleFormDesc]   = useState('')
  const [roleFormPerms,  setRoleFormPerms]  = useState<Record<string, string>>(DEFAULT_ROLE_PERMS())
  const [savingRole,     setSavingRole]     = useState(false)
  // System role rename (display_name only)
  const [renamingRoleId,    setRenamingRoleId]    = useState<string | null>(null)
  const [renameDisplayName, setRenameDisplayName] = useState('')
  const [savingRename,      setSavingRename]      = useState(false)

  // ── Org data ─────────────────────────────────────────────────────────────
  const { data: org, isLoading: orgLoading, refetch: refetchOrg } = useQuery({
    queryKey: ['org-profile', orgId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('organizations' as any)
        .select('id, name, slug, locale_default, comms_email, logo_url, brand_primary, brand_secondary, brand_accent, created_at, org_type, status')
        .eq('id', orgId)
        .single() as any)
      if (error) throw error
      return data as any
    }
  })

  const [orgName,    setOrgName]    = useState('')
  const [orgSlug,    setOrgSlug]    = useState('')
  const [orgLocale,  setOrgLocale]  = useState('')
  const [orgEmail,   setOrgEmail]   = useState('')
  const [orgType,    setOrgType]    = useState('standard')
  const [brandPrimary,   setBrandPrimary]   = useState('')
  const [brandSecondary, setBrandSecondary] = useState('')
  const [brandAccent,    setBrandAccent]    = useState('')
  const [settingStatus, setSettingStatus] = useState(false)

  // Sync local state once org loads
  const [initialized, setInitialized] = useState(false)
  if (org && !initialized) {
    setOrgName(org.name ?? '')
    setOrgSlug(org.slug ?? '')
    setOrgLocale(org.locale_default ?? 'en')
    setOrgEmail(org.comms_email ?? '')
    setOrgType(org.org_type ?? 'standard')
    setBrandPrimary(org.brand_primary ?? '#0D2240')
    setBrandSecondary(org.brand_secondary ?? '#006B3C')
    setBrandAccent(org.brand_accent ?? '#F0E4B8')
    setInitialized(true)
  }

  // ── Members ───────────────────────────────────────────────────────────────
  const { data: members, refetch: refetchMembers } = useQuery({
    queryKey: ['org-profile-members', orgId],
    queryFn: async () => {
      const { data: m } = await (supabase
        .from('organization_members' as any)
        .select('id, user_id, status, role_id, roles(name, display_name)')
        .eq('org_id', orgId) as any)
      const list = (m as any[]) ?? []
      if (list.length) {
        const ids = list.map((x: any) => x.user_id)
        const { data: profs } = await supabase.from('profiles').select('user_id, first_name, last_name, full_name, avatar_url').in('user_id', ids)
        const pmap: Record<string, { name: string; avatar?: string }> = {}
        ;(profs as any[] | null)?.forEach(p => {
          const composed = [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
          pmap[p.user_id] = { name: composed || p.full_name || p.user_id?.slice(0, 8) || '', avatar: p.avatar_url }
        })
        list.forEach((x: any) => {
          x.full_name  = pmap[x.user_id]?.name ?? x.user_id?.slice(0, 8) ?? ''
          x.avatar_url = pmap[x.user_id]?.avatar ?? null
        })
      }
      return list
    }
  })

  const { data: roles, refetch: refetchRoles } = useQuery({
    queryKey: ['org-profile-roles', orgId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('roles' as any)
        .select('id, name, display_name, description, is_system, role_permissions(module, access_level)')
        .eq('org_id', orgId)
        .order('name') as any)
      return (data || []) as any[]
    }
  })

  const { data: orgLanguages, refetch: refetchLanguages } = useQuery({
    queryKey: ['org-languages', orgId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_languages' as any)
        .select('language_code, is_active')
        .eq('org_id', orgId) as any)
      return (data || []) as any[]
    }
  })

  // ── Onboarding ────────────────────────────────────────────────────────────
  const { data: onboarding } = useQuery({
    queryKey: ['org-profile-onboarding', orgId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_onboarding' as any)
        .select('cco_assigned, brand_set, charter_uploaded, first_member, first_journalist, first_event, media_kit_uploaded')
        .eq('org_id', orgId)
        .maybeSingle() as any)
      return data as Record<string, boolean> | null
    }
  })

  // ── Billing ───────────────────────────────────────────────────────────────
  const { data: billing } = useQuery({
    queryKey: ['org-billing', orgId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_subscriptions' as any)
        .select('plan, status, billing_cycle, currency, amount_cents, next_payment_date, payment_provider, payment_method_label, notes')
        .eq('org_id', orgId)
        .maybeSingle() as any)
      return data as any
    }
  })

  // ── Actions ───────────────────────────────────────────────────────────────
  async function saveSettings() {
    setSavingSettings(true)
    try {
      const { error } = await (supabase
        .from('organizations' as any)
        .update({
          name: orgName, slug: orgSlug, locale_default: orgLocale, comms_email: orgEmail,
          org_type: orgType,
          brand_primary: brandPrimary, brand_secondary: brandSecondary, brand_accent: brandAccent,
        })
        .eq('id', orgId) as any)
      if (error) throw error
      toast({ title: 'Settings saved' })
      refetchOrg()
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSavingSettings(false)
    }
  }

  async function setOrgStatus(status: string) {
    setSettingStatus(true)
    try {
      const { error } = await (supabase
        .from('organizations' as any)
        .update({ status })
        .eq('id', orgId) as any)
      if (error) throw error
      toast({ title: `Institution ${status}` })
      refetchOrg()
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSettingStatus(false)
    }
  }

  async function uploadLogo(file: File) {
    setUploadingLogo(true)
    try {
      const ext  = file.name.split('.').pop()
      const path = `${orgId}/logo.${ext}`
      const { error: upErr } = await supabase.storage.from('org-logos').upload(path, file, { upsert: true })
      if (upErr) throw upErr
      const { data: { publicUrl } } = supabase.storage.from('org-logos').getPublicUrl(path)
      const { error } = await (supabase
        .from('organizations' as any)
        .update({ logo_url: publicUrl })
        .eq('id', orgId) as any)
      if (error) throw error
      toast({ title: 'Logo updated' })
      refetchOrg()
    } catch (err: any) {
      toast({ title: 'Logo upload failed', description: err.message, variant: 'destructive' })
    } finally {
      setUploadingLogo(false)
    }
  }

  async function deleteOrg() {
    const confirmed = window.confirm(
      `Permanently delete "${org?.name}"? This cannot be undone. All members, roles, and data will be removed.`
    )
    if (!confirmed) return
    setDeleting(true)
    try {
      const { error } = await (supabase
        .from('organizations' as any)
        .delete()
        .eq('id', orgId) as any)
      if (error) throw error
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
      onBack()
    } catch (err: any) {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' })
      setDeleting(false)
    }
  }

  async function changeRole(memberId: string, roleId: string) {
    const newRole = (roles || []).find((r: any) => r.id === roleId)
    if (newRole?.name === 'CCO') {
      const currentCCO = (members || []).find(
        (m: any) => m.roles?.name === 'CCO' && m.id !== memberId
      )
      if (currentCCO) {
        const ok = window.confirm(
          `This will reassign the Institution Admin role from "${currentCCO.full_name}". They will keep organisation access under a different role. Continue?`
        )
        if (!ok) return
        const fallback =
          (roles || []).find((r: any) => r.name === 'Media Relations') ??
          (roles || []).find((r: any) => r.name !== 'CCO')
        if (fallback) {
          await (supabase
            .from('organization_members' as any)
            .update({ role_id: fallback.id })
            .eq('id', currentCCO.id) as any)
        }
      }
    }
    const { error } = await (supabase
      .from('organization_members' as any)
      .update({ role_id: roleId })
      .eq('id', memberId) as any)
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' })
    else refetchMembers()
  }

  async function toggleMemberStatus(memberId: string, currentStatus: string) {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active'
    const { error } = await (supabase
      .from('organization_members' as any)
      .update({ status: newStatus })
      .eq('id', memberId) as any)
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' })
    else refetchMembers()
  }

  // ── Language management ───────────────────────────────────────────────────
  async function addLanguage(code: string) {
    await (supabase.from('org_languages' as any).upsert(
      { org_id: orgId, language_code: code, is_active: true },
      { onConflict: 'org_id, language_code' }
    ) as any)
    refetchLanguages()
  }

  async function removeLanguage(code: string) {
    if (code === org?.locale_default) return
    await (supabase.from('org_languages' as any)
      .delete().eq('org_id', orgId).eq('language_code', code) as any)
    refetchLanguages()
  }

  // ── Custom role management ────────────────────────────────────────────────
  function startCreateRole() {
    setEditingRoleId(null)
    setRoleFormName('')
    setRoleFormDesc('')
    setRoleFormPerms(DEFAULT_ROLE_PERMS())
    setShowRoleForm(true)
  }

  function startEditRole(role: any) {
    setEditingRoleId(role.id)
    setRoleFormName(role.name)
    setRoleFormDesc(role.description ?? '')
    const perms = DEFAULT_ROLE_PERMS()
    ;(role.role_permissions || []).forEach((p: any) => { perms[p.module] = p.access_level })
    setRoleFormPerms(perms)
    setShowRoleForm(true)
  }

  async function saveCustomRole() {
    if (!roleFormName.trim()) return
    setSavingRole(true)
    try {
      if (editingRoleId) {
        const { error: rErr } = await (supabase.from('roles' as any)
          .update({ name: roleFormName, description: roleFormDesc })
          .eq('id', editingRoleId) as any)
        if (rErr) throw rErr
        await Promise.all(MODULES.map(m =>
          (supabase.from('role_permissions' as any).upsert(
            { role_id: editingRoleId, module: m.key, access_level: roleFormPerms[m.key] ?? 'none' },
            { onConflict: 'role_id, module' }
          ) as any)
        ))
      } else {
        const { data: newRole, error: rErr } = await (supabase.from('roles' as any)
          .insert({ org_id: orgId, name: roleFormName, description: roleFormDesc, is_system: false })
          .select().single() as any)
        if (rErr) throw rErr
        await (supabase.from('role_permissions' as any)
          .insert(MODULES.map(m => ({
            role_id: (newRole as any).id,
            module: m.key,
            access_level: roleFormPerms[m.key] ?? 'none',
          }))) as any)
      }
      toast({ title: editingRoleId ? 'Role updated' : 'Custom role created' })
      setShowRoleForm(false)
      setEditingRoleId(null)
      refetchRoles()
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSavingRole(false)
    }
  }

  async function deleteCustomRole(roleId: string) {
    if (!window.confirm('Delete this custom role? Any members assigned to it will keep access until reassigned.')) return
    const { error } = await (supabase.from('roles' as any).delete().eq('id', roleId) as any)
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' })
    else { toast({ title: 'Role deleted' }); refetchRoles() }
  }

  function startRenameRole(role: any) {
    setRenamingRoleId(role.id)
    setRenameDisplayName(role.display_name ?? role.name)
  }

  async function saveRoleDisplayName() {
    if (!renamingRoleId) return
    setSavingRename(true)
    try {
      const value = renameDisplayName.trim() || null
      const { error } = await (supabase.from('roles' as any)
        .update({ display_name: value })
        .eq('id', renamingRoleId) as any)
      if (error) throw error
      toast({ title: 'Role label updated' })
      setRenamingRoleId(null)
      refetchRoles()
      queryClient.invalidateQueries({ queryKey: ['org-roles-onboarding', orgId] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSavingRename(false)
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  const initials = org?.name
    ? org.name.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase()
    : '?'

  const completedSteps = ONBOARDING_STEPS.filter(s => onboarding?.[s.key]).length

  const planColor: Record<string, string> = {
    trial: 'bg-amber-100 text-amber-800',
    individual: 'bg-teal-100 text-teal-800',
    starter: 'bg-blue-100 text-blue-800',
    professional: 'bg-purple-100 text-purple-800',
    enterprise: 'bg-[#0D2240] text-[#F0E4B8]',
  }

  if (orgLoading) return <ConorbitLoader variant="inline" />

  if (!org) return (
    <div className="p-6 text-red-600 text-sm">Organisation not found.</div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      {onBack && (
        <div className="flex items-start gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-[#0D2240] transition-colors mt-1"
          >
            <ArrowLeft size={14} />
            Institutions
          </button>
        </div>
      )}

      <div className="flex items-center gap-4">
        {/* Logo / initials */}
        <div className="relative group">
          {org.logo_url ? (
            <img
              src={org.logo_url}
              alt={org.name}
              className="w-16 h-16 rounded-xl object-contain border border-[#DDD8CE] bg-white"
            />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-[#0D2240] flex items-center justify-center text-[#F0E4B8] text-lg font-bold">
              {initials}
            </div>
          )}
          <button
            onClick={() => logoInputRef.current?.click()}
            className="absolute inset-0 rounded-xl bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
            title="Upload logo"
          >
            {uploadingLogo ? (
              <Loader2 size={16} className="text-white animate-spin" />
            ) : (
              <Upload size={16} className="text-white" />
            )}
          </button>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) uploadLogo(f) }}
          />
        </div>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-[#0D2240]">{org.name}</h2>
            <KYCStatusBadge orgId={orgId} />
            {/* Org status badge */}
            {(() => {
              const s = org.status ?? 'active'
              if (s === 'active') return null
              return (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${
                  s === 'suspended' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                }`}>
                  {s}
                </span>
              )
            })()}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-justify text-[12px] text-muted-foreground font-mono">{org.slug}</p>
            {(() => {
              const typeLabels: Record<string, { label: string; className: string }> = {
                standard:    { label: 'Standard',    className: 'bg-blue-100 text-blue-700' },
                association: { label: 'Association', className: 'bg-purple-100 text-purple-700' },
                journalist:  { label: 'Journalist',  className: 'bg-amber-100 text-amber-700' },
              }
              const t = typeLabels[org.org_type ?? 'standard'] ?? typeLabels.standard
              return (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${t.className}`}>
                  {t.label}
                </span>
              )
            })()}
          </div>
          <p className="text-justify text-[11px] text-muted-foreground mt-0.5">
            Created {new Date(org.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="w-full">
          <TabsTrigger value="overview"   className="flex-1">Overview</TabsTrigger>
          <TabsTrigger value="members"    className="flex-1">Members</TabsTrigger>
          <TabsTrigger value="roles"      className="flex-1">Roles</TabsTrigger>
          <TabsTrigger value="onboarding" className="flex-1">Onboarding</TabsTrigger>
          <TabsTrigger value="billing"    className="flex-1">Billing</TabsTrigger>
          <TabsTrigger value="kyc"        className="flex-1">KYC</TabsTrigger>
          <TabsTrigger value="charter"    className="flex-1">Charter</TabsTrigger>
          <TabsTrigger value="structure"  className="flex-1">Org Structure</TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="mt-5 space-y-6">

          {/* Basic info */}
          <div className="space-y-3">
            <h3 className="text-[11.5px] font-semibold text-muted-foreground uppercase tracking-wide">
              Basic Information
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground">Name</label>
                <Input value={orgName} onChange={e => setOrgName(e.target.value)} className="mt-1 text-sm" />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Slug</label>
                <Input value={orgSlug} onChange={e => setOrgSlug(e.target.value)} className="mt-1 text-sm font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground">Institution Type</label>
                <Select value={orgType} onValueChange={setOrgType}>
                  <SelectTrigger className="mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Institution</SelectItem>
                    <SelectItem value="association">Association</SelectItem>
                    <SelectItem value="journalist">Journalist Institution</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground">Default Language</label>
                <Select value={orgLocale} onValueChange={setOrgLocale}>
                  <SelectTrigger className="mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ALL_LANGUAGES.map(l => (
                      <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Communications Email</label>
                <Input value={orgEmail} onChange={e => setOrgEmail(e.target.value)} className="mt-1 text-sm" type="email" />
              </div>
            </div>

            {/* Organisation Languages */}
            <div className="space-y-2 pt-1">
              <div className="flex items-center gap-1.5">
                <Globe size={12} className="text-muted-foreground" />
                <label className="text-[11px] text-muted-foreground font-medium">Organisation Languages</label>
              </div>
              <p className="text-justify text-[11px] text-muted-foreground/80">
                Official languages this organisation communicates in. Individual members may use their own preferred language.
              </p>
              <div className="flex flex-wrap gap-2 mt-1">
                {(orgLanguages || []).map((lang: any) => {
                  const info    = ALL_LANGUAGES.find(l => l.code === lang.language_code)
                  const isDef   = lang.language_code === org?.locale_default
                  return (
                    <div
                      key={lang.language_code}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] border ${isDef ? 'bg-[#0D2240] text-[#F0E4B8] border-[#0D2240]' : 'bg-white border-[#DDD8CE] text-[#0D2240]'}`}
                    >
                      <span>{info?.flag ?? ''}</span>
                      <span>{info?.label ?? lang.language_code}</span>
                      {isDef && <span className="text-[10px] opacity-50 ml-0.5">default</span>}
                      {!isDef && (
                        <button
                          onClick={() => removeLanguage(lang.language_code)}
                          className="ml-0.5 opacity-60 hover:opacity-100 text-[13px] leading-none"
                          title="Remove language"
                        >×</button>
                      )}
                    </div>
                  )
                })}
                {(() => {
                  const existing  = new Set((orgLanguages || []).map((l: any) => l.language_code))
                  const available = ALL_LANGUAGES.filter(l => !existing.has(l.code))
                  if (!available.length) return null
                  return (
                    <select
                      value=""
                      onChange={e => { if (e.target.value) addLanguage(e.target.value) }}
                      className="px-2.5 py-1 border border-dashed border-[#DDD8CE] rounded-full text-[12px] bg-background cursor-pointer text-muted-foreground hover:border-[#006B3C] transition-colors"
                    >
                      <option value="">+ Add language</option>
                      {available.map(l => (
                        <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                      ))}
                    </select>
                  )
                })()}
              </div>
            </div>
          </div>

          {/* Branding */}
          <div className="space-y-3">
            <h3 className="text-[11.5px] font-semibold text-muted-foreground uppercase tracking-wide">
              Brand Colours
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Primary',   value: brandPrimary,   setter: setBrandPrimary },
                { label: 'Secondary', value: brandSecondary, setter: setBrandSecondary },
                { label: 'Accent',    value: brandAccent,    setter: setBrandAccent },
              ].map(({ label, value, setter }) => (
                <div key={label} className="space-y-1.5">
                  <label className="text-[11px] text-muted-foreground">{label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={e => setter(e.target.value)}
                      className="w-9 h-9 rounded-lg border border-[#DDD8CE] cursor-pointer p-0.5 bg-white"
                    />
                    <span className="text-[12px] font-mono text-muted-foreground">{value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button
            onClick={saveSettings}
            disabled={savingSettings}
            style={{ backgroundColor: '#006B3C' }}
            className="text-white hover:opacity-90"
          >
            {savingSettings && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>

          {/* Danger zone */}
          <div className="border border-red-200 rounded-xl p-4 space-y-4 bg-red-50/40">
            <h3 className="text-[12px] font-semibold text-red-700">Institution Controls</h3>

            {/* Status actions */}
            <div className="space-y-2">
              <p className="text-justify text-[11.5px] text-muted-foreground">
                Changing the institution's status takes immediate effect.
              </p>
              <div className="flex flex-wrap gap-2">
                {(org.status ?? 'active') !== 'active' && (
                  <button
                    onClick={() => setOrgStatus('active')}
                    disabled={settingStatus}
                    className="px-3 py-1.5 border border-green-300 text-green-700 rounded-lg text-[12px] font-medium hover:bg-green-50 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {settingStatus && <Loader2 size={12} className="animate-spin" />}
                    Reactivate Institution
                  </button>
                )}
                {(org.status ?? 'active') !== 'suspended' && (
                  <button
                    onClick={() => setOrgStatus('suspended')}
                    disabled={settingStatus}
                    className="px-3 py-1.5 border border-amber-300 text-amber-700 rounded-lg text-[12px] font-medium hover:bg-amber-50 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {settingStatus && <Loader2 size={12} className="animate-spin" />}
                    Suspend Institution
                  </button>
                )}
                {(org.status ?? 'active') !== 'restricted' && (
                  <button
                    onClick={() => setOrgStatus('restricted')}
                    disabled={settingStatus}
                    className="px-3 py-1.5 border border-orange-300 text-orange-700 rounded-lg text-[12px] font-medium hover:bg-orange-50 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {settingStatus && <Loader2 size={12} className="animate-spin" />}
                    Restrict Institution
                  </button>
                )}
              </div>
              <p className="text-justify text-[10.5px] text-muted-foreground/70">
                <strong>Suspended:</strong> Institution cannot log in or create new activity. &nbsp;
                <strong>Restricted:</strong> Read-only access, no new events or publications.
              </p>
            </div>

            <div className="border-t border-red-200 pt-3">
              <p className="text-justify text-[11.5px] text-red-600 mb-2">
                Permanently delete this organisation and all its data. This cannot be undone.
              </p>
              <Button
                variant="destructive"
                size="sm"
                onClick={deleteOrg}
                disabled={deleting}
                className="text-xs"
              >
                {deleting ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1.5" />}
                Delete Organisation
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ── Members ── */}
        <TabsContent value="members" className="mt-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users size={15} className="text-muted-foreground" />
              <span className="text-[13px] font-semibold text-[#0D2240]">Team Members</span>
              {members && (
                <span className="text-[11px] text-muted-foreground">
                  ({(members as any[]).filter((m: any) => m.status === 'active').length} active)
                </span>
              )}
            </div>
            <Button
              size="sm"
              style={{ backgroundColor: '#006B3C' }}
              className="text-white hover:opacity-90 text-xs h-8"
              onClick={() => { setAddUserRole(undefined); setAddUserOpen(true) }}
            >
              <Plus size={13} className="mr-1" />
              Add User
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!(members as any[])?.length ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10 text-muted-foreground text-sm">
                    <AlertCircle size={16} className="mx-auto mb-2 text-amber-500" />
                    No members yet.{' '}
                    <button
                      onClick={() => { setAddUserRole('CCO'); setAddUserOpen(true) }}
                      className="text-[#006B3C] hover:underline font-medium"
                    >
                      Assign Institution Admin
                    </button>
                  </TableCell>
                </TableRow>
              ) : (members as any[]).map((m: any) => (
                <TableRow key={m.id}>
                  <TableCell className="text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#0D2240]/10 flex items-center justify-center shrink-0 overflow-hidden text-[10px] font-bold text-[#0D2240]">
                        {m.avatar_url
                          ? <img src={m.avatar_url} className="w-full h-full object-cover" alt="" />
                          : (m.full_name || '?').split(' ').map((n: string) => n[0]).join('').slice(0,2).toUpperCase()
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5 leading-tight">
                          {m.full_name}
                          {m.roles?.name === 'CCO' && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#0D2240] text-[#F0E4B8] leading-tight">
                              {m.roles?.display_name ?? 'CCO'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={m.role_id}
                      onValueChange={v => changeRole(m.id, v)}
                    >
                      <SelectTrigger className="w-40 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(roles || []).map((r: any) => (
                          <SelectItem key={r.id} value={r.id}>{r.display_name ?? r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={m.status === 'active' ? 'default' : 'secondary'}
                      className="text-xs capitalize"
                    >
                      {m.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs"
                      onClick={() => toggleMemberStatus(m.id, m.status)}
                      disabled={m.roles?.name === 'CCO' && m.status === 'active'}
                      title={
                        m.roles?.name === 'CCO' && m.status === 'active'
                          ? 'Reassign the Institution Admin role before suspending this user'
                          : undefined
                      }
                    >
                      {m.status === 'active' ? 'Suspend' : 'Reactivate'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        {/* ── Roles & Permissions ── */}
        <TabsContent value="roles" className="mt-5 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[13px] font-semibold text-[#0D2240]">Roles & Permissions</h3>
              <p className="text-justify text-[11.5px] text-muted-foreground mt-0.5">
                System roles define core access levels. You can rename them to match your organisation's terminology, or create custom roles for specialised team divisions.
              </p>
            </div>
            <button
              onClick={startCreateRole}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#006B3C] text-white rounded-lg text-[12px] font-medium hover:bg-[#005A32]"
            >
              <Plus size={13} />
              New Custom Role
            </button>
          </div>

          {/* Role create / edit form */}
          {showRoleForm && (
            <div className="border border-[#006B3C]/30 bg-[#E8F5EE] rounded-xl p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Settings2 size={14} className="text-[#006B3C]" />
                <span className="text-[13px] font-semibold text-[#006B3C]">
                  {editingRoleId ? 'Edit Custom Role' : 'Create Custom Role'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-muted-foreground">Role Name *</label>
                  <input
                    value={roleFormName}
                    onChange={e => setRoleFormName(e.target.value)}
                    placeholder="e.g. Media Relations Manager"
                    className="mt-1 w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C] bg-white"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-muted-foreground">Description</label>
                  <input
                    value={roleFormDesc}
                    onChange={e => setRoleFormDesc(e.target.value)}
                    placeholder="Short description of this role's responsibilities"
                    className="mt-1 w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C] bg-white"
                  />
                </div>
              </div>

              {/* Permissions matrix */}
              <div>
                <label className="text-[11px] text-muted-foreground font-medium">Module Permissions</label>
                <div className="mt-2 border border-[#DDD8CE] rounded-xl overflow-hidden">
                  {MODULES.map((mod, i) => (
                    <div key={mod.key} className={`flex items-center justify-between px-3 py-2 ${i % 2 === 1 ? 'bg-[#F7F5F0]' : 'bg-white'} ${i < MODULES.length - 1 ? 'border-b border-[#DDD8CE]' : ''}`}>
                      <span className="text-[12px] text-[#0D2240]">{mod.label}</span>
                      <div className="flex gap-1">
                        {ACCESS_LEVELS.map(level => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setRoleFormPerms(p => ({ ...p, [mod.key]: level }))}
                            className={`px-2.5 py-0.5 rounded text-[10.5px] font-medium capitalize transition-colors ${
                              roleFormPerms[mod.key] === level
                                ? level === 'none' ? 'bg-[#DDD8CE] text-[#0D2240]'
                                  : level === 'view' ? 'bg-blue-100 text-blue-700'
                                  : level === 'edit' ? 'bg-amber-100 text-amber-700'
                                  : 'bg-[#006B3C] text-white'
                                : 'bg-transparent text-muted-foreground hover:bg-[#DDD8CE]/60'
                            }`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => { setShowRoleForm(false); setEditingRoleId(null) }}
                  className="flex-1 py-2 border border-[#DDD8CE] rounded-lg text-[12.5px] text-muted-foreground hover:bg-white"
                >
                  Cancel
                </button>
                <button
                  onClick={saveCustomRole}
                  disabled={savingRole || !roleFormName.trim()}
                  className="flex-1 py-2 bg-[#006B3C] text-white rounded-lg text-[12.5px] font-medium hover:bg-[#005A32] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingRole && <Loader2 size={13} className="animate-spin" />}
                  {editingRoleId ? 'Save Changes' : 'Create Role'}
                </button>
              </div>
            </div>
          )}

          {/* Roles list */}
          <div className="space-y-2">
            {(roles || []).map((role: any) => {
              const permsMap: Record<string, string> = {}
              ;(role.role_permissions || []).forEach((p: any) => { permsMap[p.module] = p.access_level })
              const fullCount = MODULES.filter(m => permsMap[m.key] === 'full').length
              const editCount = MODULES.filter(m => permsMap[m.key] === 'edit').length
              const viewCount = MODULES.filter(m => permsMap[m.key] === 'view').length
              const displayLabel = role.display_name ?? role.name
              const isRenaming = renamingRoleId === role.id
              return (
                <div key={role.id} className="border border-[#DDD8CE] rounded-xl px-4 py-3 space-y-2">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-semibold text-[#0D2240]">{displayLabel}</span>
                        {role.display_name && role.display_name !== role.name && (
                          <span className="text-[10px] text-muted-foreground font-mono">({role.name})</span>
                        )}
                        {role.is_system ? (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-[#0D2240] text-[#F0E4B8] px-1.5 py-0.5 rounded">
                            System
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-[#E8F5EE] text-[#006B3C] border border-[#006B3C]/20 px-1.5 py-0.5 rounded">
                            Custom
                          </span>
                        )}
                      </div>
                      {role.description && (
                        <p className="text-[11.5px] text-muted-foreground mt-0.5 truncate">{role.description}</p>
                      )}
                      <div className="flex gap-3 mt-1.5 text-[11px]">
                        {fullCount > 0 && <span className="text-[#006B3C]">{fullCount} full</span>}
                        {editCount > 0 && <span className="text-amber-600">{editCount} edit</span>}
                        {viewCount > 0 && <span className="text-blue-600">{viewCount} view</span>}
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {role.is_system ? (
                        <button
                          onClick={() => isRenaming ? setRenamingRoleId(null) : startRenameRole(role)}
                          className="flex items-center gap-1 px-2 py-1 border border-[#DDD8CE] rounded-lg hover:bg-[#F7F5F0] text-[11px] text-muted-foreground"
                          title="Rename this role for your organisation"
                        >
                          <Pencil size={11} />
                          {isRenaming ? 'Cancel' : 'Rename'}
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditRole(role)}
                            className="p-1.5 border border-[#DDD8CE] rounded-lg hover:bg-[#F7F5F0] text-muted-foreground"
                            title="Edit role"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            onClick={() => deleteCustomRole(role.id)}
                            className="p-1.5 border border-red-200 rounded-lg hover:bg-red-50 text-red-500"
                            title="Delete role"
                          >
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Inline rename form for system roles */}
                  {isRenaming && (
                    <div className="flex items-center gap-2 pt-1 border-t border-[#DDD8CE]">
                      <div className="flex-1">
                        <p className="text-justify text-[10.5px] text-muted-foreground mb-1">
                          Custom label for "{role.name}" in this organisation. The system still uses the canonical role for permissions.
                        </p>
                        <input
                          value={renameDisplayName}
                          onChange={e => setRenameDisplayName(e.target.value)}
                          placeholder={`e.g. Head of Communications`}
                          className="w-full px-3 py-1.5 border border-[#DDD8CE] rounded-lg text-[12.5px] focus:outline-none focus:border-[#006B3C] bg-white"
                        />
                      </div>
                      <button
                        onClick={saveRoleDisplayName}
                        disabled={savingRename}
                        className="px-3 py-1.5 bg-[#006B3C] text-white rounded-lg text-[12px] font-medium hover:bg-[#005A32] disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                      >
                        {savingRename && <Loader2 size={11} className="animate-spin" />}
                        Save Label
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </TabsContent>

        {/* ── Onboarding ── */}
        <TabsContent value="onboarding" className="mt-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[13px] font-semibold text-[#0D2240]">Onboarding Progress</span>
            <span className="text-[12px] text-muted-foreground">
              {completedSteps} / {ONBOARDING_STEPS.length} steps complete
            </span>
          </div>
          <div className="h-1.5 bg-[#DDD8CE] rounded-full overflow-hidden mb-5">
            <div
              className="h-full bg-[#006B3C] rounded-full transition-all"
              style={{ width: `${(completedSteps / ONBOARDING_STEPS.length) * 100}%` }}
            />
          </div>
          <ul className="space-y-2.5">
            {ONBOARDING_STEPS.map(step => {
              const done = !!onboarding?.[step.key]
              return (
                <li key={step.key} className="flex items-center gap-2.5 text-sm">
                  {done ? (
                    <CheckCircle className="w-4 h-4 text-[#006B3C] shrink-0" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-muted-foreground/30 shrink-0" />
                  )}
                  <span className={done ? 'text-foreground' : 'text-muted-foreground'}>
                    {step.label}
                  </span>
                </li>
              )
            })}
          </ul>
        </TabsContent>

        {/* ── Billing ── */}
        <TabsContent value="billing" className="mt-5">
          {!billing ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              <p>No subscription record found for this organisation.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${planColor[billing.plan] ?? 'bg-gray-100 text-gray-700'}`}>
                  {billing.plan}
                </span>
                <Badge variant={billing.status === 'active' ? 'default' : 'secondary'} className="text-xs capitalize">
                  {billing.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                {[
                  { label: 'Billing Cycle', value: billing.billing_cycle },
                  { label: 'Currency',      value: billing.currency },
                  { label: 'Amount',        value: billing.amount_cents != null ? `${(billing.amount_cents / 100).toFixed(2)} ${billing.currency}` : '' },
                  { label: 'Next Payment',  value: billing.next_payment_date ? new Date(billing.next_payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '' },
                  { label: 'Provider',      value: billing.payment_provider },
                  { label: 'Method',        value: billing.payment_method_label || '' },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-[#F7F5F0] rounded-lg px-3 py-2.5">
                    <p className="text-justify text-[10.5px] text-muted-foreground uppercase tracking-wide">{label}</p>
                    <p className="text-justify text-[13px] font-medium text-[#0D2240] mt-0.5 capitalize">{value || ''}</p>
                  </div>
                ))}
              </div>

              {billing.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-amber-800">
                  <strong>Notes:</strong> {billing.notes}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ── KYC ── */}
        <TabsContent value="kyc" className="mt-5">
          <OrgKYCPanel orgId={orgId} orgName={org.name} members={members as any[]} />
        </TabsContent>

        {/* ── Charter ── */}
        <TabsContent value="charter" className="mt-5">
          <OrgCharterView orgId={orgId} orgName={org.name} canEdit={true} />
        </TabsContent>
        <TabsContent value="structure" className="mt-5">
          <OrgUnitTree />
        </TabsContent>
      </Tabs>

      {/* Add User Sheet */}
      <CreateUserSheet
        org_id={orgId}
        org_name={org.name}
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        defaultRole={addUserRole}
        onSuccess={() => {
          refetchMembers()
          queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
        }}
      />
    </div>
  )
}

// ── KYC status badge shown in org header ──────────────────────────────────
const KYC_BADGE: Record<string, { label: string; className: string }> = {
  not_started:    { label: 'KYC Not Started', className: 'bg-red-100 text-red-700' },
  submitted:      { label: 'KYC Submitted',   className: 'bg-blue-100 text-blue-700' },
  docs_requested: { label: 'Docs Requested',  className: 'bg-amber-100 text-amber-700' },
  under_review:   { label: 'Under Review',    className: 'bg-purple-100 text-purple-700' },
  approved:       { label: 'KYC Approved',    className: 'bg-green-100 text-green-700' },
  rejected:       { label: 'KYC Rejected',    className: 'bg-red-100 text-red-700' },
}

function KYCStatusBadge({ orgId }: { orgId: string }) {
  const { data: kyc } = useQuery({
    queryKey: ['org-kyc-status', orgId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_kyc' as any)
        .select('kyc_status')
        .eq('org_id', orgId)
        .maybeSingle() as any)
      return data as { kyc_status: string } | null
    }
  })
  const status = kyc?.kyc_status ?? 'not_started'
  if (status === 'approved') return null
  const badge = KYC_BADGE[status] ?? KYC_BADGE.not_started
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${badge.className}`}>
      {badge.label}
    </span>
  )
}

// ── Org KYC management panel (KYC tab content) ────────────────────────────
const ORG_DOCS: { key: string; label: string; field: string }[] = [
  { key: 'registration_cert',    label: 'Certificate of Registration / Incorporation', field: 'registration_cert_url' },
  { key: 'tax_id',               label: 'Tax Identification Document',                 field: 'tax_id_url' },
  { key: 'proof_of_address',     label: 'Proof of Address',                            field: 'proof_of_address_url' },
  { key: 'authorized_signatory', label: 'Authorised Signatory Proof',                  field: 'authorized_signatory_url' },
]

const USER_DOC_TYPES: Record<string, string> = {
  passport:      'Passport',
  national_id:   'National ID',
  drivers_license: "Driver's Licence",
}

function OrgKYCPanel({ orgId, orgName, members }: { orgId: string; orgName: string; members: any[] }) {
  const { toast } = useToast()
  const fileRefs = Object.fromEntries(ORG_DOCS.map(d => [d.key, { current: null as HTMLInputElement | null }]))

  const [notifyOpen,      setNotifyOpen]      = useState(false)
  const [rejectOpen,      setRejectOpen]      = useState(false)
  const [missingSelected, setMissingSelected] = useState<Record<string, boolean>>({})
  const [notifyNote,      setNotifyNote]      = useState('')
  const [rejectReason,    setRejectReason]    = useState('')
  const [sending,         setSending]         = useState(false)
  const [uploadingDoc,    setUploadingDoc]    = useState<string | null>(null)

  const { data: kyc, refetch: refetchKyc } = useQuery({
    queryKey: ['org-kyc', orgId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_kyc' as any)
        .select('*')
        .eq('org_id', orgId)
        .maybeSingle() as any)
      return data as any
    }
  })

  const { data: userKycs, refetch: refetchUserKyc } = useQuery({
    queryKey: ['user-kyc', orgId],
    queryFn: async () => {
      const { data } = await (supabase
        .from('user_kyc' as any)
        .select('*')
        .eq('org_id', orgId) as any)
      return (data || []) as any[]
    }
  })

  async function updateOrgKycStatus(status: string, extra: Record<string, any> = {}) {
    const { data: { user } } = await supabase.auth.getUser()
    const { error } = await (supabase
      .from('org_kyc' as any)
      .upsert({ org_id: orgId, kyc_status: status, reviewer_id: user?.id, reviewed_at: new Date().toISOString(), ...extra }, { onConflict: 'org_id' }) as any)
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' })
    else { toast({ title: `KYC status set to: ${status}` }); refetchKyc() }
  }

  async function uploadOrgDoc(docKey: string, field: string, file: File) {
    setUploadingDoc(docKey)
    try {
      const ext  = file.name.split('.').pop()
      const path = `org/${orgId}/${docKey}-${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from('kyc-documents').upload(path, file, { upsert: true })
      if (upErr) throw upErr
      const { data: { publicUrl } } = supabase.storage.from('kyc-documents').getPublicUrl(path)
      const { error } = await (supabase
        .from('org_kyc' as any)
        .upsert({ org_id: orgId, [field]: publicUrl }, { onConflict: 'org_id' }) as any)
      if (error) throw error
      toast({ title: 'Document uploaded' })
      refetchKyc()
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' })
    } finally {
      setUploadingDoc(null)
    }
  }

  async function sendMissingDocsNotification() {
    const docs = Object.keys(missingSelected).filter(k => missingSelected[k])
    if (!docs.length) { toast({ title: 'Select at least one document', variant: 'destructive' }); return }
    setSending(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await supabase.functions.invoke('notify-kyc-missing-docs', {
        body: { org_id: orgId, missing_docs: docs, note: notifyNote },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      })
      if (res.error || (res.data as any)?.error) throw new Error(res.error?.message ?? (res.data as any).error)
      toast({ title: 'Notification sent', description: 'Email and in-app notification dispatched.' })
      setNotifyOpen(false)
      setMissingSelected({})
      setNotifyNote('')
      refetchKyc()
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSending(false)
    }
  }

  async function updateUserKyc(userKycId: string, status: string, notes?: string) {
    const { error } = await (supabase
      .from('user_kyc' as any)
      .update({ kyc_status: status, reviewer_notes: notes ?? null, reviewed_at: new Date().toISOString() })
      .eq('id', userKycId) as any)
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' })
    else { toast({ title: `Member KYC: ${status}` }); refetchUserKyc() }
  }

  const kycStatus   = kyc?.kyc_status ?? 'not_started'
  const missingDocs = (kyc?.missing_docs ?? {}) as Record<string, boolean>
  const badge       = KYC_BADGE[kycStatus] ?? KYC_BADGE.not_started

  const memberKycMap: Record<string, any> = {}
  ;(userKycs || []).forEach((u: any) => { memberKycMap[u.user_id] = u })

  return (
    <div className="space-y-8">
      {/* ── Organisation KYC ── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={15} className="text-[#0D2240]" />
            <span className="text-[13px] font-semibold text-[#0D2240]">Organisation KYC</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${badge.className}`}>
              {badge.label}
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            {kycStatus === 'submitted' && (
              <button
                onClick={() => updateOrgKycStatus('under_review')}
                className="px-3 py-1.5 text-[11.5px] border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50"
              >
                Set Under Review
              </button>
            )}
            {['submitted', 'under_review', 'docs_requested'].includes(kycStatus) && (
              <>
                <button
                  onClick={() => updateOrgKycStatus('approved')}
                  className="px-3 py-1.5 text-[11.5px] bg-[#006B3C] text-white rounded-lg hover:bg-[#005A32]"
                >
                  Approve
                </button>
                <button
                  onClick={() => setRejectOpen(true)}
                  className="px-3 py-1.5 text-[11.5px] border border-red-300 text-red-700 rounded-lg hover:bg-red-50"
                >
                  Reject
                </button>
              </>
            )}
            <button
              onClick={() => {
                const pre: Record<string, boolean> = {}
                ORG_DOCS.forEach(d => { pre[d.key] = !!missingDocs[d.key] })
                setMissingSelected(pre)
                setNotifyOpen(true)
              }}
              className="px-3 py-1.5 text-[11.5px] border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 flex items-center gap-1.5"
            >
              <ShieldAlert size={12} />
              Notify: Missing Docs
            </button>
          </div>
        </div>

        {/* Document checklist */}
        <div className="border border-[#DDD8CE] rounded-xl overflow-hidden">
          {ORG_DOCS.map((doc, i) => {
            const url     = kyc?.[doc.field]
            const missing = missingDocs[doc.key]
            return (
              <div key={doc.key} className={`flex items-center gap-3 px-4 py-3 ${i % 2 === 1 ? 'bg-[#F7F5F0]' : ''} ${i < ORG_DOCS.length - 1 ? 'border-b border-[#DDD8CE]' : ''}`}>
                {url ? (
                  <CheckCircle size={15} className="text-[#006B3C] shrink-0" />
                ) : (
                  <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${missing ? 'border-red-400' : 'border-muted-foreground/30'}`} />
                )}
                <span className="flex-1 text-[12.5px] text-[#0D2240]">
                  {doc.label}
                  {missing && !url && (
                    <span className="ml-2 text-[10px] font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">MISSING</span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  {url && (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#006B3C] hover:underline flex items-center gap-0.5">
                      <ExternalLink size={11} /> View
                    </a>
                  )}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    ref={el => { if (fileRefs[doc.key]) fileRefs[doc.key].current = el }}
                    className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadOrgDoc(doc.key, doc.field, f) }}
                  />
                  <button
                    onClick={() => fileRefs[doc.key]?.current?.click()}
                    disabled={uploadingDoc === doc.key}
                    className="px-2 py-1 text-[11px] border border-[#DDD8CE] rounded-lg hover:bg-[#F7F5F0] flex items-center gap-1 disabled:opacity-50"
                  >
                    {uploadingDoc === doc.key ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                    {url ? 'Replace' : 'Upload'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {kyc?.reviewer_notes && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-amber-800">
            <strong>Reviewer notes:</strong> {kyc.reviewer_notes}
          </div>
        )}
        {kyc?.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-[12px] text-red-700">
            <strong>Rejection reason:</strong> {kyc.rejection_reason}
          </div>
        )}
      </div>

      {/* ── Member KYC ── */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-[#0D2240]" />
          <span className="text-[13px] font-semibold text-[#0D2240]">Member Identity Verification</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Document Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!(members || []).length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-6 text-sm text-muted-foreground">
                  No members yet.
                </TableCell>
              </TableRow>
            ) : (members || []).map((m: any) => {
              const uk     = memberKycMap[m.user_id]
              const uStatus = uk?.kyc_status ?? 'not_started'
              const uBadge  = KYC_BADGE[uStatus] ?? KYC_BADGE.not_started
              return (
                <TableRow key={m.user_id}>
                  <TableCell className="text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#0D2240]/10 flex items-center justify-center shrink-0 overflow-hidden text-[9px] font-bold text-[#0D2240]">
                        {m.avatar_url
                          ? <img src={m.avatar_url} className="w-full h-full object-cover" alt="" />
                          : (m.full_name || '?').split(' ').map((n: string) => n[0]).join('').slice(0,2).toUpperCase()
                        }
                      </div>
                      {m.full_name || m.user_id?.slice(0, 8)}
                    </div>
                  </TableCell>
                  <TableCell className="text-[12px] text-muted-foreground">
                    {uk?.identity_doc_type ? USER_DOC_TYPES[uk.identity_doc_type] ?? uk.identity_doc_type : ''}
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${uBadge.className}`}>
                      {uBadge.label}
                    </span>
                  </TableCell>
                  <TableCell className="text-[11px] text-muted-foreground">
                    {uk?.submitted_at ? new Date(uk.submitted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {uk?.identity_doc_url && (
                        <a href={uk.identity_doc_url} target="_blank" rel="noopener noreferrer"
                           className="px-2 py-1 text-[11px] border border-[#DDD8CE] rounded hover:bg-[#F7F5F0] flex items-center gap-1">
                          <FileText size={11} /> Doc
                        </a>
                      )}
                      {uk && uStatus !== 'approved' && (
                        <button onClick={() => updateUserKyc(uk.id, 'approved')}
                          className="px-2 py-1 text-[11px] bg-[#006B3C] text-white rounded hover:bg-[#005A32]">
                          Approve
                        </button>
                      )}
                      {uk && uStatus !== 'rejected' && (
                        <button onClick={() => updateUserKyc(uk.id, 'docs_requested', 'Please resubmit your identity document.')}
                          className="px-2 py-1 text-[11px] border border-amber-300 text-amber-700 rounded hover:bg-amber-50">
                          Request Re-submission
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* ── Notify Missing Docs dialog ── */}
      {notifyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-xl shadow-2xl w-[480px] max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <h3 className="text-[14px] font-semibold text-[#0D2240]">Notify: Missing Documents</h3>
            <p className="text-justify text-[12px] text-muted-foreground">
              Select which documents are missing or unacceptable. The organisation's CCO will receive
              an in-app notification and an email.
            </p>
            <div className="space-y-2">
              {ORG_DOCS.map(doc => (
                <label key={doc.key} className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!missingSelected[doc.key]}
                    onChange={e => setMissingSelected(p => ({ ...p, [doc.key]: e.target.checked }))}
                    className="w-4 h-4 accent-[#006B3C]"
                  />
                  <span className="text-[12.5px]">{doc.label}</span>
                </label>
              ))}
            </div>
            <div>
              <label className="text-[11.5px] font-medium text-muted-foreground block mb-1">
                Reviewer Note (optional)
              </label>
              <textarea
                value={notifyNote}
                onChange={e => setNotifyNote(e.target.value)}
                rows={3}
                placeholder="Explain what's wrong or what format is needed…"
                className="w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[12.5px] resize-none focus:outline-none focus:border-[#006B3C]"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setNotifyOpen(false)}
                className="flex-1 px-4 py-2 border border-[#DDD8CE] rounded-lg text-[12.5px] text-muted-foreground hover:bg-[#F7F5F0]">
                Cancel
              </button>
              <button onClick={sendMissingDocsNotification} disabled={sending}
                className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg text-[12.5px] font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-1.5">
                {sending ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />}
                Send Notification
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject dialog ── */}
      {rejectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-xl shadow-2xl w-[420px] p-6 space-y-4">
            <h3 className="text-[14px] font-semibold text-red-700">Reject KYC Submission</h3>
            <div>
              <label className="text-[11.5px] font-medium text-muted-foreground block mb-1">
                Reason for rejection
              </label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Explain why this submission is being rejected…"
                className="w-full px-3 py-2 border border-red-200 rounded-lg text-[12.5px] resize-none focus:outline-none focus:border-red-400"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setRejectOpen(false)}
                className="flex-1 px-4 py-2 border border-[#DDD8CE] rounded-lg text-[12.5px] text-muted-foreground hover:bg-[#F7F5F0]">
                Cancel
              </button>
              <button
                onClick={() => { updateOrgKycStatus('rejected', { rejection_reason: rejectReason }); setRejectOpen(false) }}
                disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-[12.5px] font-medium hover:bg-red-700 disabled:opacity-50">
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
